/**
 * Nigerian phone number parsing — one parse, both representations.
 *
 * Why this exists separately from `src/app/import-voters/components/ImportVoterHelpers.tsx`:
 * that module's `normalizeNigerianPhone` returns the 11-digit local form (`0803…`) and feeds it
 * straight to the voter-import endpoint. The CustomContact API stores E.164 (`+2348…`, produced
 * server-side by libphonenumber with DefaultRegion=NG) and uses it as the duplicate key. Changing
 * the import-voters normalizer to E.164 would silently change what gets written to the voter table
 * — a cross-module data regression in a module that currently has no tests.
 *
 * So this returns BOTH forms from a single parse. They are derived from the same national
 * significant number (NSN) and therefore cannot disagree with each other.
 *
 * This is a deliberate approximation of libphonenumber, not a replacement: the server stays
 * authoritative. Always display the `phoneE164` the API returns after a write, never the value
 * guessed here — see `docs/claude/modules.md`.
 */

export interface NgPhone {
	/** True when a 10-digit national significant number was recovered. */
	ok: boolean;
	/** E.164 form, e.g. `+2348031112233`. Empty string when !ok. This is the server's dedupe key. */
	e164: string;
	/** Local form, e.g. `08031112233`. Empty string when !ok. */
	local: string;
	/** The 10-digit national significant number, e.g. `8031112233`. Empty string when !ok. */
	nsn: string;
	/** True when the input was not already in the canonical local form. */
	wasNormalized: boolean;
	/** Present when !ok, or when the number parsed but looks unusual. */
	reason?: string;
}

const FAIL = (reason: string): NgPhone => ({ ok: false, e164: '', local: '', nsn: '', wasNormalized: false, reason });

/**
 * Known Nigerian mobile network prefixes (the first 4 digits of the local form).
 * Deliberately advisory: MNO ranges are extended over time, so an unrecognised prefix produces a
 * WARNING, never a rejection. Hard-rejecting here would block legitimate new ranges and there is
 * no way for a user to work around it.
 */
const KNOWN_MOBILE_PREFIXES = new Set([
	'0701',
	'0702',
	'0703',
	'0704',
	'0705',
	'0706',
	'0707',
	'0708',
	'0709',
	'0801',
	'0802',
	'0803',
	'0804',
	'0805',
	'0806',
	'0807',
	'0808',
	'0809',
	'0810',
	'0811',
	'0812',
	'0813',
	'0814',
	'0815',
	'0816',
	'0817',
	'0818',
	'0819',
	'0901',
	'0902',
	'0903',
	'0904',
	'0905',
	'0906',
	'0907',
	'0908',
	'0909',
	'0911',
	'0912',
	'0913',
	'0915',
	'0916',
]);

/**
 * Parse anything user- or spreadsheet-supplied into a Nigerian phone number.
 *
 * Accepted shapes (after stripping spaces, dashes, brackets and dots):
 *   08031112233     11-digit local
 *   8031112233      10-digit NSN — Excel strips the leading zero from a numeric cell
 *   +2348031112233  E.164
 *   2348031112233   E.164 without the plus
 *   +23408031112233 "+234" glued onto the local form — common in pasted contact lists
 */
export const parseNgPhone = (raw: unknown): NgPhone => {
	if (typeof raw === 'number') {
		// A spreadsheet cell typed as a number has already lost its leading zero, and a long one may
		// arrive in scientific notation. Render it as a plain integer string before parsing.
		if (!Number.isFinite(raw)) return FAIL('Not a phone number');
		return parseNgPhone(BigInt(Math.trunc(raw)).toString());
	}
	if (typeof raw !== 'string') return FAIL('Not a phone number');

	const trimmed = raw.trim();
	if (!trimmed) return FAIL('Phone number is required');

	const hadPlus = trimmed.startsWith('+');
	const digits = trimmed.replace(/\D/g, '');
	if (!digits) return FAIL('Not a phone number');

	let nsn = '';
	if (digits.startsWith('234')) {
		const rest = digits.slice(3);
		// '+234' + local ('0803…') and '+234' + NSN ('803…') are both seen in real data.
		nsn = rest.length === 11 && rest.startsWith('0') ? rest.slice(1) : rest;
	} else if (digits.startsWith('0')) {
		nsn = digits.slice(1);
	} else {
		nsn = digits;
	}

	if (nsn.length !== 10) {
		return FAIL(
			nsn.length < 10
				? `Phone number is too short (${digits.length} digits)`
				: `Phone number is too long (${digits.length} digits)`
		);
	}

	const local = `0${nsn}`;
	const prefix = local.slice(0, 4);
	const wasNormalized = hadPlus || trimmed !== local;

	return {
		ok: true,
		e164: `+234${nsn}`,
		local,
		nsn,
		wasNormalized,
		reason: KNOWN_MOBILE_PREFIXES.has(prefix) ? undefined : `Unrecognised network prefix (${prefix})`,
	};
};

/** Convenience: the E.164 form, or '' when the input is not a usable Nigerian number. */
export const toE164 = (raw: unknown): string => parseNgPhone(raw).e164;

/**
 * The client-side duplicate key. Uses the NSN so `08031112233`, `+2348031112233`,
 * `2348031112233` and `8031112233` all collapse to one contact — matching how the server
 * de-duplicates on its stored E.164 value.
 */
export const phoneDedupeKey = (raw: unknown): string => parseNgPhone(raw).nsn;
